import React, { useCallback, useEffect, useRef, useState } from 'react'
import deepEqual from 'react-fast-compare'
import { Button, Flex, Popover, Space, Tooltip } from 'antd'
import { Loading3QuartersOutlined, PartitionOutlined, WarningOutlined } from '@ant-design/icons'
import { usePQueryBuilder } from '@pushly/aqe'
import { numberWithCommas } from '../../../_utils/utils'
import { getUnsafeExecutionText } from './unsafe-execution'
import { isUnsafeExecution } from './unsafe-execution'
import type { ReachCalculatedField } from './unsafe-execution'
import { PendingEdit, stalenessOf } from './pending-edit'
import type { OrgReachResult } from './estimate-org-reach'

/**
 * The org reach lookup, injected so this sibling stays a pure reader of the live
 * document. Org reach is a per-domain fan-out that returns a count per domain —
 * there is no per-channel breakdown at this level.
 */
export type EstimateOrgReach = (
    domainIds: number[],
    query: Record<string, unknown>,
    targetedChannels: string[],
    opts?: { cancellationKey?: string },
) => Promise<OrgReachResult>

export interface SegmentOrgReachEstimateProps {
    orgId: number
    /** The domains the org segment targets. No domains means nothing to estimate. */
    domainIds: number[]
    /** The segment's own channels; empty means every channel. */
    targetedChannels: string[]
    calculatedFields: readonly ReachCalculatedField[]
    /** Display names for the selected domains, so the breakdown labels each row. */
    domainNames: Record<number, string>
    estimateOrgReach: EstimateOrgReach
}

type ReachState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'unsafe' }
    | { status: 'error' }
    | { status: 'success'; total: number; byDomain: Record<number, number> }

const DEBOUNCE_MS = 320

let instanceCounter = 0

/**
 * A live audience-size estimate for the org segment being built, composed into the
 * conditions well's actions slot beside the builder. It reads the current document
 * straight from the builder and never holds a copy of it.
 *
 * The estimate fans out across the selected domains, so its identity is the
 * document AND the domain set: a domain change with an unchanged document targets a
 * different audience and must refetch.
 *
 * States:
 *  - idle: no domains selected, or no conditions yet — renders nothing
 *  - loading: an estimate is in flight
 *  - success: the summed total and a per-domain breakdown
 *  - error: the lookup failed, offered with a retry — never a silent zero, which a
 *    real total of 0 is indistinguishable from otherwise
 *  - unsafe: a delayed property the platform has not computed for every selected
 *    domain yet — the size is masked and no lookup is issued
 */
export const SegmentOrgReachEstimate: React.FC<SegmentOrgReachEstimateProps> = ({
    domainIds,
    targetedChannels,
    calculatedFields,
    domainNames,
    estimateOrgReach,
}) => {
    const { isValid, conditionCount, serialize, revision, editingCount } = usePQueryBuilder()

    // Read at call time, not captured, so the debounced fetch always serializes the
    // document and reads the domain set as they stand when the timer fires.
    const serializeRef = useRef(serialize)
    serializeRef.current = serialize
    const domainIdsRef = useRef(domainIds)
    domainIdsRef.current = domainIds

    const [state, setState] = useState<ReachState>({ status: 'idle' })

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // The document and domain set the last request was issued for, compared to skip
    // a refetch when neither changed.
    const lastFetchedRef = useRef<{ document: Record<string, unknown>; domainIds: number[] } | null>(null)
    // Bumped for every request and every abandonment, so a response that resolves
    // after a newer one was issued can tell it has been superseded and drop itself.
    const generationRef = useRef(0)
    // Stable per instance so the service's own cancellation cancels the previous
    // in-flight request the moment a newer one starts.
    const cancellationKeyRef = useRef<string>()
    if (!cancellationKeyRef.current) {
        cancellationKeyRef.current = `segment-org-reach-estimate-${(instanceCounter += 1)}`
    }

    const runEstimate = useCallback(
        async (document: Record<string, unknown>, currentDomainIds: number[]) => {
            const generation = (generationRef.current += 1)
            lastFetchedRef.current = { document, domainIds: currentDomainIds }
            setState({ status: 'loading' })

            const res = await estimateOrgReach(currentDomainIds, { filters: document }, targetedChannels, {
                cancellationKey: cancellationKeyRef.current,
            })

            // A newer request has started, or this one was cancelled in favor of
            // it — either way its result is stale and must not be shown.
            if (generation !== generationRef.current || res.cancelled) return

            if (!res.ok || !res.byDomain) {
                setState({ status: 'error' })
                return
            }

            const byDomain = res.byDomain
            const total = Object.values(byDomain).reduce((sum, value) => sum + value, 0)
            setState({ status: 'success', total, byDomain })
        },
        [targetedChannels, estimateOrgReach],
    )

    // A domain change with an unchanged document still has to re-run the effect, so
    // the domain set joins `revision` as a trigger.
    const domainIdsKey = domainIds.join(',')

    useEffect(() => {
        if (domainIds.length === 0 || conditionCount === 0) {
            // Nothing to estimate. Abandon any in-flight response and clear the
            // dedupe memory so the next real query fetches.
            generationRef.current += 1
            lastFetchedRef.current = null
            setState({ status: 'idle' })
            return
        }

        // A condition being edited is in the document already — the editor writes every keystroke
        // through — but it is not yet what the reader has asked for, and costing it means a batch of
        // per-domain invocations for a value they are still choosing. The estimate waits for the edit
        // to be committed, and says so rather than presenting the pre-edit number as current.
        if (editingCount > 0) return

        // An invalid document does not describe a query the endpoint can answer;
        // leave whatever is already shown in place and wait for it to be fixed.
        if (!isValid) return

        const document = serializeRef.current()

        if (isUnsafeExecution(document, calculatedFields)) {
            // The number would be wrong until the platform computes it for every
            // selected domain, so it is masked and no request is issued. Clearing the
            // dedupe memory means a later safe edit fetches rather than dedupes.
            generationRef.current += 1
            lastFetchedRef.current = null
            setState({ status: 'unsafe' })
            return
        }

        const last = lastFetchedRef.current
        if (last && deepEqual(document, last.document) && deepEqual(domainIds, last.domainIds)) return

        if (debounceRef.current !== null) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null
            runEstimate(document, domainIds)
        }, DEBOUNCE_MS)

        return () => {
            if (debounceRef.current !== null) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
            }
        }
        // `revision` stands in for the document identity — it moves on every builder
        // mutation, including meta-only edits `queryHash` would miss — and
        // `domainIdsKey` carries a change to the selected domains the document
        // identity alone would not.
        // `editingCount` is watched alongside `revision` because committing an edit does not mutate
        // the document — the keystrokes already did — so closing the editor moves only this, and it is
        // what releases the estimate.
    }, [revision, editingCount, domainIdsKey, conditionCount, isValid, calculatedFields, runEstimate])

    const handleRetry = useCallback(() => {
        lastFetchedRef.current = null
        runEstimate(serializeRef.current(), domainIdsRef.current)
    }, [runEstimate])

    const staleness = stalenessOf(editingCount)

    if (state.status === 'idle') {
        // Nothing has been estimated yet and an edit is in flight, which is the first condition on a
        // new segment. Saying what will happen beats an empty space beside a condition the reader can
        // see, and beats a number for a value they are still choosing.
        if (staleness === 'pending-edit' && conditionCount > 0) {
            return <span className="reach-label">Reach updates when you save this condition</span>
        }

        return null
    }

    if (state.status === 'loading') {
        return (
            <Flex align="center" gap={8}>
                <Loading3QuartersOutlined spin={true} />
                <span>Calculating reach…</span>
            </Flex>
        )
    }

    if (state.status === 'unsafe') {
        return (
            <PendingEdit staleness={staleness}>
                <Space size={4}>
                    <span className="reach-tooltip">
                        <Tooltip title={getUnsafeExecutionText()}>
                            <WarningOutlined className="info-icon error-icon" />
                        </Tooltip>
                    </span>
                    <span className="reach-estimate">--</span>
                    <span className="reach-label">Subscribers</span>
                </Space>
            </PendingEdit>
        )
    }

    if (state.status === 'error') {
        return (
            <Flex align="center" gap={8}>
                <span>Reach is currently not available</span>
                <Button type="link" size="small" onClick={handleRetry}>
                    Retry
                </Button>
            </Flex>
        )
    }

    const domains = Object.keys(state.byDomain).map(Number)
    // The per-domain breakdown is meaningless when nobody is reached, and there is
    // nothing to break down with only a single domain.
    const showBreakdown = state.total !== 0 && domainIds.length >= 2

    return (
        <PendingEdit staleness={staleness}>
            <Space size={4}>
                <span className="reach-estimate">{numberWithCommas(state.total)}</span>
                <span className="reach-label">Subscribers</span>
                {showBreakdown && (
                    <Popover
                        title="Reach Estimates by Domain"
                        overlayClassName="audience-domain-breakdown-popover"
                        content={domains.map((domainId) => (
                            <div key={domainId} className="reach-breakdown-row">
                                <span className="reach-breakdown-label">{domainLabel(domainId, domainNames)}:</span>
                                <span className="reach-breakdown-value">
                                    {numberWithCommas(state.byDomain[domainId])}
                                </span>
                            </div>
                        ))}
                        placement="left"
                    >
                        <PartitionOutlined />
                    </Popover>
                )}
            </Space>
        </PendingEdit>
    )
}

function domainLabel(domainId: number, domainNames: Record<number, string>): string {
    return domainNames[domainId] ?? `Domain ${domainId}`
}
